import SessionLib from './sessionLib';
import config from '../config';
import { rfc3339StringToDateTimeString } from './stringLib';

/**
 * Async function to get data from integral api.
 * 
 * @param {string} endpoint
 *    Endpoint to fetch. Takes full url f.i. "`${config.api.address}/fca/firms`"
 * @param {string} method 
 *    Verb for the call. F.i. GET, PUT
 * @param {string} postData
 *    Jason object to post.
 * @param {bool} throwOnFail
 *    Whether an exception should be raised for a on-200 response code.
 */
export async function getFromApi (
  endpoint,
  method = 'GET',
  postData = null,
  throwOnFail = false
) {
  
  // We may want to use a test-token to cut out cognito on the integral side.
  let authHeaderValue   = null
  if(config.api.hasOwnProperty('token') && config.api.token !== "") {
    authHeaderValue = 'bearer ' + config.api.token;
  }
  else {
    // First get the user's token.
    // If we have no authenticated user it will throw an error.
    const session = await SessionLib.currentSession();
    // get different tokens.
    const idToken     = session.getIdToken().jwtToken;
    const accessToken = session.getAccessToken().jwtToken;
    authHeaderValue   = 'id_token ' + idToken + ', access_token ' + accessToken;
  }

  const settings = {
    method: method,
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'Authorization': authHeaderValue,
    },
    body: postData
  };
  const fetchResponse = await fetch(endpoint, settings);
  const code          = fetchResponse.status;
  const data          = await fetchResponse.json();
  // Do we need tot hrow an error?
  if (code !== 200 && throwOnFail) {
    let message = `Received #${code} from api with message: "${fetchResponse.body}"`;
    // See if there is error text in the body.
    if (
      data.hasOwnProperty('error') &&
      data.error.hasOwnProperty('message')
    ) {
      message = `Received #${code} from api with message: "${data.error.message}"`;
    }
    throw new Error(message);
  }
  return { code, data };
}

/**
 * Function to get data from integral api. Returns a promise.
 * 
 * @param {string} endpoint
 *    Endpoint to fetch. Takes full url f.i. "`${config.api.address}/fca/firms`"
 * @param {string} method 
 *    Verb for the call. F.i. GET, PUT
 * @param {string} postData
 *    Jason object to post.
 */
export function getPromiseFromApi(endpoint, method = 'GET', postData = null) {
  return new Promise((resolve, reject) => {
    // First get the user's token.
    // If we have no authenticated user it will throw an error.
    let code =999;
    SessionLib.currentSession()
      .then(
        session => {
          // We may want to use a test-token to cut out cognito on the integral side.
          let authHeaderValue   = null
          if(config.api.hasOwnProperty('token') && config.api.token !== "") {
            authHeaderValue = 'bearer ' + config.api.token;
          }
          else {
            // get different tokens.
            const idToken     = session.getIdToken().jwtToken;
            const accessToken = session.getAccessToken().jwtToken;
            authHeaderValue   = 'id_token ' + idToken + ', access_token ' + accessToken;
          }
          // Then we build request settings:
          let settings = {
            method: method,
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
              'Authorization': authHeaderValue,
            }
          };
          if(postData !== null) {
            settings.body = JSON.stringify(postData);
          }
          // And attempt to fetch.
          fetch(endpoint, settings)
            .then(
              response => {
                code = response.status;
                return response.json();
              }
            )
            .then(
              data => {
                resolve({code, data});
              }
            )
            .catch(
              error => {
                reject('Error retrieving data: ' + error.message);
              }
            )
        }
      )
      .catch(error => {
        // We failed to get a session.
        // We may just not have a user.
        if (error === 'No current user') {
          // Try and get it anonymously.
          // Then we build request settings:
          let settings = {
            method: method,
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
            }
          };
          if(postData !== null) {
            settings.body = JSON.stringify(postData);
          }
          // And attempt to fetch.
          fetch(endpoint, settings)
            .then(
              response => {
                code = response.status;
                return response.json();
              }
            )
            .then(
              data => {
                resolve({code, data});
              }
            )
            .catch(
              error => {
                reject('Error retrieving data: ' + error.message);
              }
            )
        }
        else {
          reject('Error retrieving session: ' + error.message);
        }
      });
  });
}

/**
 * Turns an array of name-value objs into an object with name: value properties.
 *
 * @param {array} array
 *    Array of object containing keys "name", and "value"
 */
export function nameValueArrayToObject(array) {
  let result = {};
  for (let i = 0; i < array.length; i++) {
    // Some users (federated) have an identities property that contains a json
    // string.
    if (array[i].name === 'identities') {
      try {
        result[array[i].name] = JSON.parse(array[i].value);
      }
      catch (error) {
        result[array[i].name] = array[i].value;
      }
    }
    else {
      result[array[i].name] = array[i].value;
    }
  }
  return result;
}

/**
 * Parses user data as returned from Integral into a more convenient structure.
 *
 * @param {*} data 
 */
export function parseUser(data) {
  let user = {};
  // Copy the simple ones.
  user.userName = data.userName;
  user.userStatus = data.userStatus;
  user.enabled = data.enabled;
  user.userCreated = rfc3339StringToDateTimeString(data.userCreated);
  user.userLastModified = rfc3339StringToDateTimeString(data.userLastModified);
  // Flatten attributes.
  user.attributes = nameValueArrayToObject(data.attributes);
  // Determine email.
  // See if the user is federated.
  user.email = 'UNKNOWN';
  user.isStaff = false;
  user.isfederated = false;
  if (user.userStatus === 'EXTERNAL_PROVIDER') {
    user.isfederated = true;
    // Have a look at the identities attribute.
    if (
      user.attributes.hasOwnProperty('identities') && 
      Array.isArray(user.attributes.identities) &&
      user.attributes.identities.length > 0
    ) {
      const identity = user.attributes.identities[0];
      // We should now have some data.
      if (identity.hasOwnProperty('userId')) {
        user.email = user.attributes.identities[0].userId;
      }
      if (
        identity.hasOwnProperty('providerName') &&
        identity.providerName === 'Cirencester-Friendly'
      ) {
        user.isStaff = true;
      }
    }
  }
  else {
    user.email = user.attributes.email;
  }
  // Determine type
  user.type = [];
  if (user.isStaff) {
    user.type = ['staff'];
  }
  else {
    // Type is stored as bitflags. Parse it here.
    // Convert type to an int.
    user.typeInt = parseInt(user.attributes['custom:type'], 2);
    if (isNaN(user.typeInt)) {
      user.type.push('INVALID');
      user.typeInt = 0;
    }
    else {
      // Do some bitwise compares.
      if(user.typeInt & 0b10000000) {
        user.type.push('member');
      }
      if(user.typeIntpeInt & 0b01000000) {
        user.type.push('staff');
        user.isStaff = true;
      }
      if(user.typeInt & 0b00100000) {
        user.type.push('ifa');
      }
      if(user.typeInt & 0b00010000) {
        user.type.push('paraplanner');
      }
      if(user.typeInt & 0b00001000) {
        user.type.push('administrator');
      }
    }
  }
  return user;
}

/**
 * An array of user types.
 * @typedef {Object} userType
 * @property {string} code
 *    Binary code for user type.
 * @property {string} friendlyName
 *    Human readable description of user type.
 */

/**
 * Returns an array of valid (settable) user types.
 *
 * @returns {userType[]} userTypes
 *    Settable user types.
 */
export function getUserTypes(includeIAmA = false) {
  let result = [];

  if (includeIAmA) {
    result.push(
      {
        code: '00000000',
        friendlyName: 'I am a(n)...',
      }
    );
  }
  
  result = result.concat([
    {
      code: '10000000',
      friendlyName: 'Member',
    },
    {
      code: '00100000',
      friendlyName: 'Financial Adviser',
    }
  ]);
  // User can't register as any of these as it is driven from SSG.
  if (!includeIAmA) {
    result = result.concat([
      {
        code: '00010000',
        friendlyName: 'Paraplanner',
      },
      {
        code: '00001000',
        friendlyName: 'Administrator',
      },
      {
        code: '00110000',
        friendlyName: 'Financial Adviser and Paraplanner',
      },
      {
        code: '00011000',
        friendlyName: 'Paraplanner and Administrator',
      },
    ]);
}

  return result;
}

export function formatIntegralDate(date) {
  // Test length.
  if (date === null || date.length !== 8) {
    return date;
  }
  return date.substring(6,8) + '/' + date.substring(4,6) + '/' + date.substring(0,4)
}